type SupportPayloadWithAttachments = {
  attachments?: File[];
  attachment_files?: string[];
};

type SupportAttentionFilter = 'awaiting_agent' | 'never_agent_answered';
type SupportWaitingBucket = 'under_12h' | '12_to_24h' | 'over_24h';
type SupportCollaborationFilter = 'needs_attention' | 'mentioned_me' | 'watched_by_me' | 'handoff_to_me';
type SupportPriorityApiTier = 'tier4' | 'tier5';
type SupportCustomerAccountFilter = Api.Support.SupportCustomerAccountFilter;

type SupportExportParams = {
  format?: string;
  status?: string;
  priority?: string | string[];
  category?: number | number[];
  assignee?: 'me' | 'unassigned';
  assigned_to?: string;
  attention?: SupportAttentionFilter;
  waiting_bucket?: SupportWaitingBucket;
  collaboration?: SupportCollaborationFilter;
  customer_account?: SupportCustomerAccountFilter;
  api_tier?: SupportPriorityApiTier;
  search?: string;
  date_from?: string;
  date_to?: string;
  ordering?: string;
  ticket_numbers?: string[];
};

type SupportTicketQueryParams = {
  status?: string;
  priority?: string[];
  category?: number[];
  search?: string;
  date_from?: string;
  date_to?: string;
  date_range?: [string, string];
  assignee?: 'me' | 'unassigned';
  assigned_to?: string;
  created_by?: number;
  attention?: SupportAttentionFilter;
  waiting_bucket?: SupportWaitingBucket;
  collaboration?: SupportCollaborationFilter;
  customer_account?: SupportCustomerAccountFilter;
  api_tier?: SupportPriorityApiTier;
  ai_intervention?: 'pending';
  page?: number;
  page_size?: number;
  ordering?: string;
};

const MAX_SUPPORT_SEARCH_QUERY_LENGTH = 200;
const MAX_SUPPORT_ROUTE_SUBJECT_LENGTH = 255;
// eslint-disable-next-line no-control-regex
const SUPPORT_SEARCH_CONTROL_CHARS_RE = /[\x00-\x1F\x7F]/g;
const SUPPORT_SEARCH_HTML_TAG_RE = /<[^>]+>/g;
const SUPPORT_SEARCH_ATTACK_MARKERS_RE = /--+|\/\*|\*\/|\.{2,}/g;
const SUPPORT_SEARCH_STRAY_SLASH_RE = /(^|\s)\/+|\/+(?=\s|$)/g;
const SUPPORT_SEARCH_UNSAFE_CHARS_RE = /[^\w\u0600-\u06FF\s./@+-]/g;
const SUPPORT_ROUTE_SUBJECT_UNSAFE_CHARS_RE = /[^\w\u0600-\u06FF\s.,:/@+?!-]/g;
const SUPPORT_TICKET_NUMBER_RE = /^TK-(?:\d{6}-[A-Z0-9]{6}|\d{8})$/;
const SUPPORT_POSITIVE_INTEGER_RE = /^\d+$/;
const CUSTOMER_ADMIN_PAGE_MAX = 100;
const CUSTOMER_ADMIN_PAGE_SIZE_MAX = 50;
const CUSTOMER_ADMIN_REPORT_FRAME_MAX = 2160;
const CUSTOMER_ADMIN_DATE_RANGE_MAX_MS = 366 * 24 * 60 * 60 * 1000;
const CUSTOMER_ADMIN_TRACKING_MAX_LENGTH = 255;
const CUSTOMER_ADMIN_TRACKING_RE = /^[A-Za-z0-9._:/-]+$/;
const CUSTOMER_ADMIN_ACTIVITY_WINDOWS = new Set<Api.Support.CustomerAdminActivityWindow>(['1h', '6h', '12h', '24h', '72h', '30d']);
const CUSTOMER_ADMIN_PAYMENT_PURCHASE_ITEMS = new Set<Api.Support.CustomerAdminPaymentPurchaseItem>(['api', 'credit_package', 'tier1', 'tier2', 'tier2_30', 'tier2_90', 'tier2_180', 'tier2_365', 'tier3']);
const CUSTOMER_ADMIN_PAYMENT_STATES = new Set<Api.Support.CustomerAdminPaymentState>(['draft', 'locked', 'processing', 'confirmed', 'failed']);
const CUSTOMER_ADMIN_AFFILIATE_RECORD_TYPES = new Set<Api.Support.CustomerAdminAffiliateRecordType>(['all', 'commission', 'withdrawal']);
const CUSTOMER_ADMIN_COMMISSION_STATUSES = new Set<Api.Support.CustomerAdminCommissionStatus>(['pending', 'confirmed', 'cancelled']);
const CUSTOMER_ADMIN_WITHDRAWAL_STATUSES = new Set<Api.Support.CustomerAdminWithdrawalStatus>(['pending', 'confirmed', 'completed', 'rejected']);
const CUSTOMER_ADMIN_REPORT_STATUSES = new Set<Api.Support.CustomerAdminReportStatus>(['pending', 'completed', 'failed']);
const CUSTOMER_ADMIN_REPORT_LANGUAGES = new Set<Api.Support.CustomerAdminReportLanguage>(['en-US', 'fa-IR']);

function stripSupportSearchAttackMarkers(query: string): string {
  return query.replace(SUPPORT_SEARCH_ATTACK_MARKERS_RE, ' ').replace(SUPPORT_SEARCH_STRAY_SLASH_RE, ' ');
}

export function normalizeSupportSearchQuery(query: string): string {
  return stripSupportSearchAttackMarkers(
    query
    .slice(0, MAX_SUPPORT_SEARCH_QUERY_LENGTH)
    .replace(SUPPORT_SEARCH_CONTROL_CHARS_RE, ' ')
    .replace(SUPPORT_SEARCH_HTML_TAG_RE, ' ')
  )
    .replace(SUPPORT_SEARCH_UNSAFE_CHARS_RE, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function normalizeSupportTicketQueryValue(value: unknown): string | null {
  const rawValue = Array.isArray(value) ? value[0] : value;
  if (typeof rawValue !== 'string') return null;

  const ticketNumber = rawValue.trim().toUpperCase();
  return SUPPORT_TICKET_NUMBER_RE.test(ticketNumber) ? ticketNumber : null;
}

export function normalizeSupportRouteSubjectQueryValue(value: unknown): string {
  const rawValue = Array.isArray(value) ? value[0] : value;
  if (typeof rawValue !== 'string') return '';

  return rawValue
    .slice(0, MAX_SUPPORT_ROUTE_SUBJECT_LENGTH)
    .replace(SUPPORT_SEARCH_CONTROL_CHARS_RE, ' ')
    .replace(SUPPORT_SEARCH_HTML_TAG_RE, ' ')
    .replace(SUPPORT_ROUTE_SUBJECT_UNSAFE_CHARS_RE, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function normalizeSupportRouteCategoryQueryValue(value: unknown): number | null {
  const rawValue = Array.isArray(value) ? value[0] : value;
  if (typeof rawValue !== 'string' && typeof rawValue !== 'number') return null;

  const categoryValue = String(rawValue).trim();
  if (!SUPPORT_POSITIVE_INTEGER_RE.test(categoryValue)) return null;

  const categoryId = Number(categoryValue);
  return Number.isSafeInteger(categoryId) && categoryId > 0 ? categoryId : null;
}

export function buildSupportAttachmentPayload<T extends SupportPayloadWithAttachments>(
  payload: T,
  uploadedFileIds: string[]
): Omit<T, 'attachments'> & { attachment_files?: string[] } {
  const { attachments: _attachments, ...apiPayload } = payload;

  if (uploadedFileIds.length > 0) {
    return {
      ...apiPayload,
      attachment_files: uploadedFileIds
    };
  }

  return apiPayload;
}

export function buildSupportReplyRequestData(reply: Api.Support.CreateReplyData) {
  const { ai_reply_idempotency_key, ...data } = reply;
  const hasSource = typeof data.ai_source_message_id === 'string' && Boolean(data.ai_source_message_id.trim());
  const hasMode = typeof data.ai_apply_mode === 'string';
  const hasKey =
    typeof ai_reply_idempotency_key === 'string' && Boolean(ai_reply_idempotency_key.trim());
  const hasAnyAttribution =
    data.ai_source_message_id !== undefined ||
    data.ai_apply_mode !== undefined ||
    ai_reply_idempotency_key !== undefined;

  if (hasAnyAttribution && !(hasSource && hasMode && hasKey)) {
    throw new Error('Incomplete AI reply attribution');
  }

  return hasAnyAttribution
    ? { data, idempotencyKey: ai_reply_idempotency_key }
    : { data };
}

export function normalizeSupportAttentionFilter(value: unknown): SupportAttentionFilter | null {
  const rawValue = Array.isArray(value) ? value[0] : value;
  return rawValue === 'awaiting_agent' || rawValue === 'never_agent_answered' ? rawValue : null;
}

export function normalizeSupportWaitingBucket(value: unknown): SupportWaitingBucket | null {
  const rawValue = Array.isArray(value) ? value[0] : value;
  return rawValue === 'under_12h' || rawValue === '12_to_24h' || rawValue === 'over_24h' ? rawValue : null;
}

export function normalizeSupportCollaborationFilter(value: unknown): SupportCollaborationFilter | null {
  const rawValue = Array.isArray(value) ? value[0] : value;
  return rawValue === 'needs_attention' || rawValue === 'mentioned_me' || rawValue === 'watched_by_me' || rawValue === 'handoff_to_me'
    ? rawValue
    : null;
}

export function normalizeSupportCustomerAccountFilter(value: unknown): SupportCustomerAccountFilter | null {
  const rawValue = Array.isArray(value) ? value[0] : value;
  return rawValue === 'free' ||
    rawValue === 'plus' ||
    rawValue === 'zero' ||
    rawValue === 'tier0' ||
    rawValue === 'tier1' ||
    rawValue === 'tier2' ||
    rawValue === 'tier3' ||
    rawValue === 'tier4' ||
    rawValue === 'tier5'
    ? rawValue
    : null;
}

export function normalizeSupportPriorityApiTier(value: unknown): SupportPriorityApiTier | null {
  const rawValue = Array.isArray(value) ? value[0] : value;
  return rawValue === 'tier4' || rawValue === 'tier5' ? rawValue : null;
}

export function buildSupportExportParams<T extends SupportExportParams>(
  params?: T
): Record<string, unknown> {
  if (!params) return {};

  const {
    format,
    assigned_to: _assignedTo,
    attention,
    waiting_bucket,
    collaboration,
    customer_account,
    api_tier,
    ...apiParams
  } = params;
  const safeParams: Record<string, unknown> = { ...apiParams };
  const safeAttention = normalizeSupportAttentionFilter(attention);
  const safeWaitingBucket = normalizeSupportWaitingBucket(waiting_bucket);
  const safeCollaboration = normalizeSupportCollaborationFilter(collaboration);
  const safeCustomerAccount = normalizeSupportCustomerAccountFilter(customer_account);
  const safeApiTier = normalizeSupportPriorityApiTier(api_tier);

  if (safeAttention) safeParams.attention = safeAttention;
  if (safeWaitingBucket) safeParams.waiting_bucket = safeWaitingBucket;
  if (safeCollaboration) safeParams.collaboration = safeCollaboration;
  if (safeCustomerAccount) safeParams.customer_account = safeCustomerAccount;
  if (safeApiTier) safeParams.api_tier = safeApiTier;
  if (format) safeParams.export_format = format;

  return safeParams;
}

export function buildSupportTicketQueryParams(
  params?: SupportTicketQueryParams
): Record<string, unknown> {
  if (!params) return {};

  const {
    priority,
    category,
    date_range,
    assigned_to: _assignedTo,
    created_by: _createdBy,
    assignee,
    attention,
    waiting_bucket,
    collaboration,
    customer_account,
    api_tier,
    ...apiParams
  } = params;

  const queryParams: Record<string, unknown> = { ...apiParams };

  if (typeof params.search === 'string') {
    const safeSearch = normalizeSupportSearchQuery(params.search);
    if (safeSearch) {
      queryParams.search = safeSearch;
    } else {
      delete queryParams.search;
    }
  }

  if (assignee === 'me' || assignee === 'unassigned') {
    queryParams.assignee = assignee;
  }

  const safeAttention = normalizeSupportAttentionFilter(attention);
  if (safeAttention) queryParams.attention = safeAttention;

  const safeWaitingBucket = normalizeSupportWaitingBucket(waiting_bucket);
  if (safeWaitingBucket) queryParams.waiting_bucket = safeWaitingBucket;

  const safeCollaboration = normalizeSupportCollaborationFilter(collaboration);
  if (safeCollaboration) queryParams.collaboration = safeCollaboration;

  const safeCustomerAccount = normalizeSupportCustomerAccountFilter(customer_account);
  if (safeCustomerAccount) queryParams.customer_account = safeCustomerAccount;

  const safeApiTier = normalizeSupportPriorityApiTier(api_tier);
  if (safeApiTier) queryParams.api_tier = safeApiTier;

  if (date_range?.[0]) {
    queryParams.date_from = date_range[0];
  }

  if (date_range?.[1]) {
    queryParams.date_to = date_range[1];
  }

  if (priority?.length) {
    queryParams['priority[]'] = priority;
  }

  if (category?.length) {
    queryParams['category[]'] = category;
  }

  return queryParams;
}

function boundedCustomerAdminInteger(value: unknown, fallback: number, max: number): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isSafeInteger(parsed)) return fallback;
  return Math.min(max, Math.max(1, parsed));
}

function buildCustomerAdminPaginationParams(filters?: Api.Support.CustomerAdminPaginationFilters): Record<string, unknown> {
  return {
    page: boundedCustomerAdminInteger(filters?.page, 1, CUSTOMER_ADMIN_PAGE_MAX),
    page_size: boundedCustomerAdminInteger(filters?.page_size, 20, CUSTOMER_ADMIN_PAGE_SIZE_MAX)
  };
}

function normalizeCustomerAdminDate(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function addCustomerAdminDateRange(
  params: Record<string, unknown>,
  filters?: Pick<Api.Support.CustomerAdminDateRangeFilters, 'date_from' | 'date_to'>
): void {
  const dateFrom = normalizeCustomerAdminDate(filters?.date_from);
  const dateTo = normalizeCustomerAdminDate(filters?.date_to);

  if (dateFrom && dateTo) {
    const rangeMs = new Date(dateTo).getTime() - new Date(dateFrom).getTime();
    if (rangeMs < 0 || rangeMs > CUSTOMER_ADMIN_DATE_RANGE_MAX_MS) return;
  }

  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
}

export function buildCustomerAdminActivityParams(window: Api.Support.CustomerAdminActivityWindow = '24h'): { window: Api.Support.CustomerAdminActivityWindow } {
  return { window: CUSTOMER_ADMIN_ACTIVITY_WINDOWS.has(window) ? window : '24h' };
}

export function buildCustomerAdminPaymentParams(filters?: Api.Support.CustomerAdminPaymentFilters): Record<string, unknown> {
  const params = buildCustomerAdminPaginationParams(filters);
  if (!filters) return params;

  if (filters.purchase_item && CUSTOMER_ADMIN_PAYMENT_PURCHASE_ITEMS.has(filters.purchase_item)) {
    params.purchase_item = filters.purchase_item;
  }
  if (filters.state && CUSTOMER_ADMIN_PAYMENT_STATES.has(filters.state)) params.state = filters.state;
  for (const booleanFilter of ['confirmed', 'failed', 'has_error'] as const) {
    if (typeof filters[booleanFilter] === 'boolean') params[booleanFilter] = filters[booleanFilter];
  }

  const tracking = typeof filters.tracking === 'string' ? filters.tracking.trim() : '';
  if (tracking && tracking.length <= CUSTOMER_ADMIN_TRACKING_MAX_LENGTH && CUSTOMER_ADMIN_TRACKING_RE.test(tracking)) {
    params.tracking = tracking;
  }

  addCustomerAdminDateRange(params, filters);
  return params;
}

export function buildCustomerAdminAffiliateParams(filters?: Api.Support.CustomerAdminAffiliateFilters): Record<string, unknown> {
  const params = buildCustomerAdminPaginationParams(filters);
  const recordType = filters?.record_type;
  const status = filters?.status;
  params.record_type = recordType && CUSTOMER_ADMIN_AFFILIATE_RECORD_TYPES.has(recordType) ? recordType : 'all';

  let statusAllowed = false;
  if (status) {
    if (params.record_type === 'commission') {
      statusAllowed = CUSTOMER_ADMIN_COMMISSION_STATUSES.has(status as Api.Support.CustomerAdminCommissionStatus);
    } else if (params.record_type === 'withdrawal') {
      statusAllowed = CUSTOMER_ADMIN_WITHDRAWAL_STATUSES.has(status as Api.Support.CustomerAdminWithdrawalStatus);
    } else {
      statusAllowed = CUSTOMER_ADMIN_COMMISSION_STATUSES.has(status as Api.Support.CustomerAdminCommissionStatus)
        || CUSTOMER_ADMIN_WITHDRAWAL_STATUSES.has(status as Api.Support.CustomerAdminWithdrawalStatus);
    }
  }
  if (statusAllowed) params.status = status;

  addCustomerAdminDateRange(params, filters);
  return params;
}

export function buildCustomerAdminReportParams(filters?: Api.Support.CustomerAdminReportFilters): Record<string, unknown> {
  const params = buildCustomerAdminPaginationParams(filters);
  if (filters?.status && CUSTOMER_ADMIN_REPORT_STATUSES.has(filters.status)) params.status = filters.status;
  return params;
}

export function buildCustomerAdminReportCreateData(data: Api.Support.CustomerAdminReportCreateData): Api.Support.CustomerAdminReportCreateData {
  return {
    frame: boundedCustomerAdminInteger(data.frame, 24, CUSTOMER_ADMIN_REPORT_FRAME_MAX),
    language: CUSTOMER_ADMIN_REPORT_LANGUAGES.has(data.language) ? data.language : 'fa-IR'
  };
}

export function buildSupportAICustomerFeedbackData(
  payload: Api.Support.SupportAICustomerFeedbackRequest
): Api.Support.SupportAICustomerFeedbackRequest {
  const data: Api.Support.SupportAICustomerFeedbackRequest = { vote: payload.vote };

  if (payload.reason_code !== undefined) data.reason_code = payload.reason_code;
  if (payload.comment !== undefined) data.comment = payload.comment;

  return data;
}

export function buildSupportAIHumanReviewData(
  payload: Api.Support.SupportAIHumanReviewRequest
): Api.Support.SupportAIHumanReviewRequest {
  const data: Api.Support.SupportAIHumanReviewRequest = {};

  if (payload.response_id !== undefined) data.response_id = payload.response_id;
  if (payload.reason_code !== undefined) data.reason_code = payload.reason_code;

  return data;
}
